import React from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent, trackButtonClick } from '../../utils/analytics';

export function OrganiserLinksModal({ organiserLinks, onClose }) {
  const navigate = useNavigate();

  const handleCopyLink = async (url, inputElement) => {
    try {
      // Try modern Clipboard API first (requires HTTPS or localhost)
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
          await navigator.clipboard.writeText(url);
          return; // Success
        } catch (clipboardError) {
          // Clipboard API failed, fall through to fallback
          console.warn('Clipboard API failed, trying fallback:', clipboardError);
        }
      }
      
      // Fallback: use execCommand with the input element
      if (inputElement) {
        inputElement.focus();
        inputElement.select();
        inputElement.setSelectionRange(0, 99999); // For mobile devices
        
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            return; // Success
          }
        } catch (execError) {
          console.warn('execCommand failed:', execError);
        }
      }
      
      // Last resort: create temporary textarea element
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          return; // Success
        }
      } catch (execError) {
        document.body.removeChild(textArea);
        throw execError;
      }
      
      throw new Error('All copy methods failed');
    } catch (error) {
      console.error('Failed to copy link:', error);
      trackEvent('Organiser Link Copy - Error', {
        squad_id: organiserLinks.squad_id,
        link_type: url === organiserLinks.public_url ? 'public' : 'admin',
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
      // Show the URL in an alert so user can copy manually
      alert(`Please copy this link manually:\n\n${url}`);
    }
  };

  const handleCopyLinkWithTracking = async (url, inputElement, linkType) => {
    await handleCopyLink(url, inputElement);
    trackEvent('Organiser Link Copied', {
      squad_id: organiserLinks.squad_id,
      link_type: linkType,
    });
  };

  const handleContinueFromModal = () => {
    trackButtonClick('Organiser Modal - I\'ve Copied It', {
      squad_id: organiserLinks.squad_id,
    });
    // Mark admin link as shown for this squad in sessionStorage (prevents double-render)
    sessionStorage.setItem(`admin_link_shown_${organiserLinks.squad_id}`, 'true');
    // Close modal and redirect to public URL (not admin URL)
    onClose();
    navigate(organiserLinks.public_url);
  };

  const handleOpenOrganiserView = () => {
    trackButtonClick('Organiser Modal - Open Organiser View', {
      squad_id: organiserLinks.squad_id,
    });
    // Mark admin link as shown for this squad in sessionStorage
    sessionStorage.setItem(`admin_link_shown_${organiserLinks.squad_id}`, 'true');
    // Close modal and redirect to admin URL
    onClose();
    navigate(organiserLinks.admin_url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-md rounded-lg bg-gray-900 border border-gray-800 p-6 shadow-xl">
        <h2 className="mb-4 text-2xl font-bold text-white">
          Organiser link (keep this private)
        </h2>
        
        <div className="mb-6 space-y-4">
          {/* Public Link */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-300">
              Public link (safe to share)
            </label>
            <div className="flex gap-2">
              <input
                id="public-link-input"
                type="text"
                value={organiserLinks.public_url}
                readOnly
                onClick={(e) => e.target.select()}
                className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#4ADE80]"
              />
              <button
                onClick={() => {
                  const input = document.getElementById('public-link-input');
                  handleCopyLinkWithTracking(organiserLinks.public_url, input, 'public');
                }}
                className="rounded-lg bg-[#4ADE80] px-4 py-2 text-sm font-semibold text-[#050505] transition-colors hover:bg-[#3bc96a]"
              >
                Copy
              </button>
            </div>
          </div>

          {/* Admin Link */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-300">
              Organiser admin link
            </label>
            <div className="mb-2 rounded-lg bg-yellow-900/20 border border-yellow-800/50 p-3 text-sm text-yellow-200">
              Anyone with this link can edit squad info.
            </div>
            <div className="flex gap-2">
              <input
                id="admin-link-input"
                type="text"
                value={organiserLinks.admin_url}
                readOnly
                onClick={(e) => e.target.select()}
                className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#4ADE80]"
              />
              <button
                onClick={() => {
                  const input = document.getElementById('admin-link-input');
                  handleCopyLinkWithTracking(organiserLinks.admin_url, input, 'admin');
                }}
                className="rounded-lg bg-[#4ADE80] px-4 py-2 text-sm font-semibold text-[#050505] transition-colors hover:bg-[#3bc96a]"
              >
                Copy
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleOpenOrganiserView}
            className="flex-1 rounded-lg bg-[#4ADE80] px-6 py-3 font-semibold text-[#050505] transition-colors hover:bg-[#3bc96a]"
          >
            Open organiser view now
          </button>
          <button
            onClick={handleContinueFromModal}
            className="flex-1 rounded-lg bg-[#4ADE80] px-6 py-3 font-semibold text-[#050505] transition-colors hover:bg-[#3bc96a]"
          >
            I've copied it
          </button>
        </div>
      </div>
    </div>
  );
}
